"use client";

import { createContext, useContext } from "react";

import { frequentHomeCities, pastDestinations } from "../_logic/actions/place-actions";
import { getCityHotelRates } from "../_logic/actions/hotel-rate-actions";
import { cached, keep, kept } from "../_logic/client-cache";
import { FREQUENT_CITIES_TTL_MS, HOTEL_RATE_CACHE_S } from "../_logic/constants";
import { hotelRateKey } from "../_logic/hotel-rates";
import type { PlaceOption } from "../_logic/places";
import type { HotelRate } from "../_logic/types";

/**
 * Where the estimator reads what it does not hold itself: venue hotel rates and the org's
 * place history. Fares come with the estimate (a saved search, or a live one).
 */
export interface EstimatorSources {
  /**
   * A city's venue hotel rate.
   * @throws When the lookup fails, so it is asked again next time.
   */
  hotelRate: (city: string, code: string | null) => Promise<HotelRate>;
  /** The org's past destinations, as quick picks. */
  pastDestinations: (orgId: number | null) => Promise<PlaceOption[]>;
  /** The org's frequent home cities, as quick picks. */
  frequentHomeCities: (orgId: number | null) => Promise<PlaceOption[]>;
}

/**
 * The signed-in org's sources: the estimator's server actions, each asked once per page (and
 * hotel rates kept in this browser across visits).
 */
export const LIVE_SOURCES: EstimatorSources = {
  hotelRate: (city, code) => {
    const key = hotelRateKey(city, code);
    return cached(key, HOTEL_RATE_CACHE_S * 1000, async () => {
      const held = kept<HotelRate>(key);
      if (held && Date.now() - held.at < HOTEL_RATE_CACHE_S * 1000) return held.value;
      const result = await getCityHotelRates([{ city, code }]);
      const rate = result.success ? result.data[city] : undefined;
      // Thrown, so a failed lookup is asked again next time instead of kept.
      if (!rate || rate.status === "error") throw new Error(`hotel rates for ${city}`);
      keep(key, rate);
      return rate;
    });
  },
  // Once per page and org: every time a picker opens reuses it, and another org asks afresh.
  pastDestinations: (orgId) => cached(`past-destinations:${orgId}`, FREQUENT_CITIES_TTL_MS, pastDestinations),
  frequentHomeCities: (orgId) => cached(`frequent-home-cities:${orgId}`, FREQUENT_CITIES_TTL_MS, frequentHomeCities),
};

const EstimatorSourcesContext = createContext<EstimatorSources>(LIVE_SOURCES);

/**
 * Supplies the estimator's sources. Without it the estimator reads {@link LIVE_SOURCES}; the
 * public sample passes fixed ones so it never calls an action.
 */
export const EstimatorSourcesProvider = EstimatorSourcesContext.Provider;

/**
 * The estimator's sources.
 * @returns The provided sources, else {@link LIVE_SOURCES}.
 * @throws Never.
 */
export function useEstimatorSources(): EstimatorSources {
  return useContext(EstimatorSourcesContext);
}
